import React, { useRef, useState, useEffect } from "react";
import Button from "../../Components/Button";
import Input from "@components/_Revamp/Input";
import { styled } from "@linaria/react";
import { isEmpty } from "lodash";
import { PRODUCT_CODE_API, postValidateAdviser } from "@api/index";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { themeDefault } from "src/theme-revamp";
import {
  FinancialAdviser,
  updateAgentCode,
  updateAgentName,
  updateAgentResponce,
  updateAgentSourceOfBusiness,
  updateAutoAssignAgent,
} from "@redux/slices/SHIELD_STARTER/step4Slice";
import { setInvalidArcCodeFromUrl, setLoadingState } from "@redux/slices/loadingSlice";
import { SCREEN_SIZE } from "@static/screen-size";
import { trackOnLoad } from "@pages/ShieldStarter/Helpers/dataAnalyticsHelpers";
import { mask } from "@utils/mask";

export interface FormHeaderContainerProps {
  margin?: string;
  maxWidth?: string;
}

const AdviserContainer = styled.div<FormHeaderContainerProps>`
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0px auto;
  border-radius: 24px;
  padding: 24px 16px;
  background-color: #fff;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  padding: "0 64px 0 64px";
  margin-top: 64px;
  flex-direction: column;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
    align-items: center;
    margin-top: 24px;
  }
`;

const InputInnerContainer = styled.div`
  width: 100%;
  #far-code{
    color: #000;
  }
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    width: 60%;
  }
`;

const AdviserContainerHeader = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  line-height: 140%;
  text-align: center;
  color: #434343;
  flex: none;
  order: 0;
  flex-grow: 1;
`;

const AdviserContainerDescription = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  color: #8a8988;
  flex: none;
  order: 0;
  flex-grow: 1;
  margin-top: 16px;
`;

const InputLabel = styled.div`
  margin-bottom: 18px;
  align-self: flex-start;
`;

const AdviserStatus = styled.div`
  align-self: flex-start;
  margin-top: 10px;
  height: 17px;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => (props.color === "red" ? "#C50000" : "#00AC95")};
  @media screen and (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 80px;
  display: flex;
  justify-content: center;
  @media screen and (max-width: 768px) {
  }
`;

function AdviserValidater() {
  const history = useHistory();
  const dispatch = useAvivaDispatch();
  const [adviser, setAdviser] = useState("");
  const [adviserName, setAdviserName] = useState("");
  const [isValidated, setIsValidated] = useState(false);
  const [error, setError] = useState("");
  const agentCodeRef = useRef<null | HTMLDivElement>(null);
  const currentAgentCode = useAvivaSelector(
    (state) => state.shieldStarterStep4.agentCode
  );
  const currentAgentName = useAvivaSelector(
    (state) => state.shieldStarterStep4.agentName
  );
  const agentCodeFromUrl = useAvivaSelector(
    (state) => state.shieldStarterStep4.agentCodeFromUrl
  );

  const clearData = () => {
    setAdviserName("");
    setError("");
  };

  async function validateAgentCode() {
    dispatch(setLoadingState(true));

    clearData();

    try {
      const { data } = await postValidateAdviser(adviser, PRODUCT_CODE_API.SHIELD_STARTER);

      if (
        data?.data &&
        data?.data?.fullName &&
        data?.data?.agentTerminatedFlag !== "Y"
      ) {
        setIsValidated(true);
        const agentName = mask(data?.data?.fullName);
        setAdviserName(
          `${agentName} has been assigned as your Financial Adviser Representative for this policy`
        );
        dispatch(updateAutoAssignAgent(FinancialAdviser.USER_PREFERRED));
        dispatch(updateAgentCode(adviser));
        dispatch(updateAgentName(agentName));
        dispatch(
          updateAgentSourceOfBusiness(data?.data?.agentSourceOfBusiness)
        );
        dispatch(updateAgentResponce(data?.data));
        dispatch(setInvalidArcCodeFromUrl(false))
        dispatch(setLoadingState(false));
        return;
      }

      setError("We couldn't find an adviser with the code you provided. Try another code.");
      dispatch(setLoadingState(false));
    } catch {
      dispatch(setLoadingState(false));
      setIsValidated(false);
      history.push(ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR);
    }
  }

  useEffect(() => {
    if (isEmpty(agentCodeFromUrl)) {
      agentCodeRef?.current?.focus();
    } else {
      setAdviser(formatString(agentCodeFromUrl));
    }
    trackOnLoad("sss_financial_adviser_code_validation_form");
  }, []);

  useEffect(() => {
    if (currentAgentCode && currentAgentName) {
      setAdviser(currentAgentCode);
      setIsValidated(true);
      setAdviserName(
        `${currentAgentName} has been assigned as your Financial Adviser Representative for this policy`
      );
    }
  }, [currentAgentCode, currentAgentName]);

  function formatString(str: string) {
    let result = "";
    for (let i = 0; i < str.length; i += 4) {
      result += str.slice(i, i + 4) + "  ";
    }
    return result.trim(); // Remove trailing space
  }

  return (
    <>
      <AdviserContainer>
        <AdviserContainerHeader>
          Enter your Financial Adviser
        </AdviserContainerHeader>
        <AdviserContainerHeader>
          Representative&apos;s code
        </AdviserContainerHeader>
        <AdviserContainerDescription>
          The code should have 8 digits (e.g. 3764 9835).
        </AdviserContainerDescription>
        <InputContainer>
          <InputInnerContainer>
            <InputLabel>Financial Adviser Representative Code</InputLabel>
            <Input
              disabled={!isEmpty(agentCodeFromUrl)}
              margin={0}
              ref={agentCodeRef}
              fontWeight={600}
              value={adviser}
              onChange={(e) => {
                if (error) setError("");
                let value = e.target.value;
                value = value.replaceAll("  ", "");

                if (value.length > 8) return;
                const isNum = /^\d+$/.test(value);
                if (value && !isNum) return;

                value = formatString(value);
                setAdviser(value || "");
              }}
              invalid={error !== ""}
            />
            <AdviserStatus color={error && !adviserName ? "red" : "green"}>
              {error || adviserName}
            </AdviserStatus>
          </InputInnerContainer>
          <Button
            variant="secondary"
            width="30%"
            label={"Find adviser"}
            disabled={
              error !== "" || adviser.trim() === "" || adviser.length < 10
            }
            onClick={validateAgentCode}
          />
        </InputContainer>
      </AdviserContainer>
      <ButtonContainer>
        <Button
          onClick={() => {
            history.push(ROUTE_PATH.SHIELD_STARTER_REVIEW);
          }}
          width={"190px"}
          variant={"default"}
          theme={themeDefault}
          label={"Continue"}
          disabled={error !== "" || !isValidated}
        ></Button>
      </ButtonContainer>
    </>
  );
}

export default AdviserValidater;
